import { useState } from "react";
import {
  FormField,
  TextInput,
  SelectInput,
  PasswordInput,
  CheckboxInput,
  ModalHeader,
  ModalFooter,
  ErrorAlert
} from "./FormComponents";

// Form configuration
const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "user", label: "User" },
  { value: "viewer", label: "Viewer" }
];

const AUTH_TYPE_OPTIONS = [
  { value: "password", label: "Password" },
  { value: "sso", label: "SSO" }
];

const SSO_PROVIDER_OPTIONS = [
  { value: "", label: "Select provider" },
  { value: "google", label: "Google" },
  { value: "microsoft", label: "Microsoft" }
];

const INITIAL_FORM_STATE = {
  first_name: "",
  last_name: "",
  email: "",
  role: "viewer",
  auth_type: "password",
  // Default: email the invitee a link to set their own password (matches the
  // "Invite" intent). Uncheck to set a password for them manually instead.
  send_setup_email: true,
  password: "",
  sso_provider: "",
  is_active: true
};

export default function InviteMemberModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));

    // Clear password if switching to SSO
    if (name === "auth_type" && value === "sso") {
      setFormData((prev) => ({ ...prev, password: "" }));
    }
  };

  // Emailing a set-password link only applies to password auth.
  const invite = formData.auth_type === "password" && formData.send_setup_email;

  const buildPayload = () => {
    const payload = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      role: formData.role,
      auth_type: formData.auth_type,
      is_active: formData.is_active
    };

    if (formData.auth_type === "password") {
      if (invite) {
        // No password: the invitee sets their own via the emailed link.
        payload.send_setup_email = true;
      } else {
        payload.password = formData.password;
      }
    }

    // Add sso_provider if auth_type is sso and provider is specified
    if (formData.auth_type === "sso" && formData.sso_provider) {
      payload.sso_provider = formData.sso_provider;
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await onSuccess(buildPayload());
      setFormData(INITIAL_FORM_STATE);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to invite member. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <ModalHeader title="Invite Team Member" onClose={onClose} />

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <ErrorAlert message={error} />

          <FormField label="First Name" required>
            <TextInput
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="John"
              required
            />
          </FormField>

          <FormField label="Last Name" required>
            <TextInput
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              placeholder="Doe"
              required
            />
          </FormField>

          <FormField label="Email Address" required>
            <TextInput
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john.doe@example.com"
              required
            />
          </FormField>

          <FormField label="Role" required>
            <SelectInput
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              options={ROLE_OPTIONS}
              required
            />
          </FormField>

          <FormField label="Authentication Type" required>
            <SelectInput
              id="auth_type"
              name="auth_type"
              value={formData.auth_type}
              onChange={handleChange}
              options={AUTH_TYPE_OPTIONS}
              required
            />
          </FormField>

          {formData.auth_type === "password" && (
            <>
              <CheckboxInput
                id="send_setup_email"
                name="send_setup_email"
                checked={formData.send_setup_email}
                onChange={handleChange}
                label="Email a set-password link (recommended)"
              />

              {invite ? (
                <p className="text-sm text-gray-500 -mt-2">
                  We'll email{" "}
                  <span className="font-medium text-gray-700">
                    {formData.email || "the invitee"}
                  </span>{" "}
                  a secure link to set their own password and sign in.
                </p>
              ) : (
                <FormField label="Password" required>
                  <PasswordInput
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter password"
                    required
                  />
                </FormField>
              )}
            </>
          )}

          {formData.auth_type === "sso" && (
            <FormField label="SSO Provider">
              <SelectInput
                id="sso_provider"
                name="sso_provider"
                value={formData.sso_provider}
                onChange={handleChange}
                options={SSO_PROVIDER_OPTIONS}
              />
            </FormField>
          )}

          <CheckboxInput
            id="is_active"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            label="Active user (can access the platform)"
          />

          <ModalFooter
            onCancel={onClose}
            loading={loading}
            submitText="Invite Member"
            loadingText="Inviting..."
          />
        </form>
      </div>
    </div>
  );
}
